import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown, LogOut, ArrowLeft, ChevronLeft, ChevronRight, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DOC_TYPES, fmonth, run, shiftMonth, thisMonth, useLoad } from "../lib/util";
import { Btn, Badge } from "../components/kit";
import { Logo } from "./Landing";
import { SchoolCtx } from "../school/ctx";
import Overview from "../school/Overview";
import DocSection, { DocList } from "../school/DocSection";
import Forms from "../school/Forms";
import Comensais from "../school/Comensais";
import Withdrawals from "../school/Withdrawals";
import CalendarSection from "../school/CalendarSection";
import Agro from "../school/Agro";
import Products from "../school/Products";
import Settings from "../school/Settings";
import { MonthBanner, MonthActions, maxMonth } from "../school/MonthStatus";
import { MAPIA_WHATSAPP, waLink } from "../lib/whatsapp";

const SECTIONS = [
  ["overview", "Visão geral"], ["forms", "Formulários"], ["comensais", "Comensais"], ["withdrawals", "Retiradas semanais"],
  ["calendar", "Calendário escolar"], ["documents", "Documentos"], ["agro", "Agricultura familiar"], ["products", "Produtos e correlação"], ["settings", "Relatórios e configurações"],
];

function Body({ section }) {
  if (section.startsWith("doc:")) return <DocSection type={section.slice(4)} />;
  const C = { overview: Overview, forms: Forms, comensais: Comensais, withdrawals: Withdrawals, calendar: CalendarSection, documents: () => <DocList />, agro: Agro, products: Products, settings: Settings }[section];
  return <C />;
}

function BaseToggle({ base, month, onChange }) {
  const set = async (b) => {
    if (b !== base && (await run(() => api.put(`/s/month-settings/${month}`, { base: b }), b === "real" ? "Totais do mês agora usam as retiradas" : "Totais do mês agora usam o Modo IA"))) onChange();
  };
  return (
    <div className="flex rounded-lg bg-white/10 p-0.5 text-xs font-semibold" data-testid="base-toggle">
      {[["estimativa", "A — Modo IA"], ["real", "B — Retiradas"]].map(([v, l]) => (
        <button key={v} data-testid={`base-${v}`} onClick={() => set(v)} className={`px-3 py-1.5 rounded-md transition-colors ${base === v ? "bg-[#FF6500] text-white" : "text-slate-300 hover:text-white"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function SchoolApp() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [month, setMonth] = useState(localStorage.getItem("mapia_month") || thisMonth());
  const [section, setSection] = useState("overview");
  const [tick, setTick] = useState(0);
  const bump = () => setTick((t) => t + 1);
  const [school, setSchool] = useState(null);
  const [blocked, setBlocked] = useState(null);
  useEffect(() => {
    api.get("/s/school").then((r) => setSchool(r.data)).catch((e) => (e?.response?.status === 402 ? setBlocked(errMsg(e)) : toast.error(errMsg(e))));
  }, []);
  const ready = !!school;
  const [ms] = useLoad(async () => (ready ? (await api.get("/s/months")).data : null), [tick, ready]);
  const max = ms && !ms.super ? maxMonth(ms) : null;
  const monthOk = !!ms && (ms.super || month <= max);
  const [products, reloadProducts] = useLoad(async () => (ready ? (await api.get("/s/products")).data : null), [tick, ready]);
  const [stock] = useLoad(async () => (monthOk ? (await api.get("/s/stock", { params: { month } })).data : null), [month, tick, monthOk]);
  const [contact, setContact] = useState(null);
  useEffect(() => {
    if (max && month > max) setMonth(max);
  }, [max, month]);
  useEffect(() => {
    if (ms && !ms.super && !ms.closed.some((c) => c.month === month) && month !== ms.work_month) setMonth(ms.work_month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms?.work_month, ms?.closed.length]);
  useEffect(() => {
    if (blocked) api.get("/public/contact").then((r) => setContact(r.data.support_whatsapp || MAPIA_WHATSAPP)).catch(() => setContact(MAPIA_WHATSAPP));
  }, [blocked]);
  useEffect(() => {
    localStorage.setItem("mapia_month", month);
  }, [month]);
  useEffect(() => {
    window.addEventListener("mapia:products", reloadProducts);
    return () => window.removeEventListener("mapia:products", reloadProducts);
  }, [reloadProducts]);
  const leave = () => {
    localStorage.removeItem("mapia_school");
    nav("/admin");
  };
  if (blocked) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4" data-testid="school-blocked">
      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 max-w-md text-center space-y-4">
        <Logo />
        <h1 className="font-heading text-2xl font-bold text-[#0B192C]">Unidade bloqueada</h1>
        <p className="text-sm text-slate-600" data-testid="school-blocked-message">{blocked}</p>
        <p className="text-xs text-slate-500">Após a confirmação do pagamento, o time MapIA libera o acesso.</p>
        <div className="flex flex-wrap justify-center gap-2">
          {contact && <a data-testid="blocked-whatsapp-button" href={waLink(contact, `Olá, time MapIA! Sou da unidade bloqueada e quero regularizar o pagamento / enviar o comprovante. Meu acesso: ${user?.email || ""}`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold text-sm px-4 py-2 transition-colors"><MessageCircle className="w-4 h-4" />Falar com o time MapIA</a>}
          <Btn variant="outline" data-testid="blocked-logout-button" onClick={() => logout().then(() => nav("/login"))}><LogOut className="w-4 h-4" />Sair</Btn>
        </div>
      </div>
    </div>
  );
  if (!school) return <div className="min-h-screen grid place-items-center text-slate-500">Carregando escola…</div>;
  const navBtn = (key, label, n) => (
    <button key={key} data-testid={`nav-${key.replace(":", "-")}`} onClick={() => setSection(key)}
      className={`shrink-0 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap ${section === key ? "bg-[#FF6500] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      {n && <span className={`mr-1.5 inline-grid place-items-center w-5 h-5 rounded-full text-[11px] ${section === key ? "bg-white/25" : "bg-[#1E3E62] text-white"}`}>{n}</span>}
      {label}
    </button>
  );
  return (
    <SchoolCtx.Provider value={{ month, school, products: products || [], reloadProducts, stock, tick, bump, setSection }}>
      <div className="min-h-screen pb-32 sm:pb-24">
        <header className="bg-[#0B192C] text-white">
          <div className="max-w-[1400px] mx-auto px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-3 justify-between">
            <div className="flex items-center gap-5">
              <Logo light />
              <div className="hidden md:block border-l border-white/15 pl-5">
                <div className="font-heading font-bold leading-tight" data-testid="school-name">{school.name}</div>
                <div className="text-xs text-slate-400">{school.ua ? `UA ${school.ua}` : school.code} · {school.municipio}/{school.uf}{school.regional ? ` · ${school.regional}` : ""}</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 bg-white/10 rounded-lg px-1 py-0.5">
                <button data-testid="month-prev" className="p-1.5 hover:text-[#FF8A3D]" onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft className="w-4 h-4" /></button>
                <input data-testid="month-input" type="month" value={month} max={max || undefined} onChange={(e) => e.target.value && setMonth(max && e.target.value > max ? max : e.target.value)} className="bg-transparent text-sm font-semibold w-[150px] [color-scheme:dark]" />
                <button data-testid="month-next" disabled={!!max && month >= max} className="p-1.5 hover:text-[#FF8A3D] disabled:opacity-30 disabled:cursor-not-allowed" onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight className="w-4 h-4" /></button>
              </div>
              {stock && <BaseToggle base={stock.base} month={month} onChange={bump} />}
              {school.acting ? (
                <Btn variant="ghost" data-testid="back-admin-button" className="text-white hover:bg-white/10" onClick={leave}><ArrowLeft className="w-4 h-4" />Painel admin</Btn>
              ) : (
                <Btn variant="ghost" data-testid="logout-button" className="text-white hover:bg-white/10" onClick={() => logout().then(() => nav("/login"))}><LogOut className="w-4 h-4" />Sair</Btn>
              )}
            </div>
          </div>
          {school.acting && <div className="bg-[#FF6500] text-center text-xs font-semibold py-1" data-testid="acting-banner">Super administrador acessando os dados desta escola ({user.email}) — ações registradas no histórico</div>}
        </header>
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="max-w-[1400px] mx-auto px-4 py-2 flex gap-1.5 overflow-x-auto scrollbar-thin" data-testid="docs-menu">
            {Object.entries(DOC_TYPES).map(([k, v]) => navBtn(`doc:${k}`, v.short, v.n))}
          </div>
          <div className="max-w-[1400px] mx-auto px-4 pb-2 flex gap-1 overflow-x-auto scrollbar-thin border-t border-slate-100 pt-2" data-testid="sections-menu">
            {SECTIONS.map(([k, l]) => navBtn(k, l))}
          </div>
        </div>
        <main className="max-w-[1400px] mx-auto px-4 py-6">
          <div className="flex items-center gap-2 mb-4 text-sm text-slate-500">
            <span className="font-semibold text-[#0B192C]">{fmonth(month).replace(/^./, (c) => c.toUpperCase())}</span>·
            <Badge tone={stock?.base === "real" ? "navy" : "orange"} data-testid="base-badge">Base dos totais: {stock?.base === "real" ? "B — Retiradas" : "A — Modo IA"}</Badge>
          </div>
          <div className="mb-4"><MonthBanner ms={ms} month={month} /></div>
          {monthOk ? <Body section={section} /> : <div className="text-slate-500 text-sm">Carregando mês…</div>}
        </main>
        <footer className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200">
          <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500 hidden sm:block">Ofício, capa, cardápio diário, controle diário e mensal no modelo oficial + notas e cardápio anexados.</span>
            {ms && <MonthActions ms={ms} month={month} acting={school.acting} onChange={bump} />}
          </div>
        </footer>
      </div>
    </SchoolCtx.Provider>
  );
}
